import { cn } from '@/lib/utils';

export interface SpinnerProps {
  className?: string;
  /** Visible label for screen readers. Defaults to "Loading". */
  label?: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeMap: Record<NonNullable<SpinnerProps['size']>, string> = {
  sm: 'h-4 w-4 border-2',
  md: 'h-6 w-6 border-2',
  lg: 'h-10 w-10 border-[3px]',
};

/**
 * Lightweight indeterminate spinner used as the default `<Suspense>` fallback
 * across the app.
 */
export function Spinner({ className, label = 'Loading', size = 'md' }: SpinnerProps) {
  return (
    <span
      role="status"
      aria-live="polite"
      className={cn('inline-flex items-center justify-center', className)}
    >
      <span
        className={cn(
          'animate-spin rounded-full border-muted border-t-primary',
          sizeMap[size],
        )}
        aria-hidden="true"
      />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Full-viewport spinner used by route-level Suspense boundaries. */
export function PageSpinner({ label }: { label?: string }) {
  return (
    <div className="flex min-h-[40vh] w-full items-center justify-center">
      <Spinner size="lg" label={label} />
    </div>
  );
}
